import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, ArrowLeft, Code, Zap, Key, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APIDocs = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [tier, setTier] = useState('startup');
  const [apiKey, setApiKey] = useState(null);
  const [generating, setGenerating] = useState(false);

  const handleGenerateKey = async () => {
    if (!email || !orgName) {
      toast.error('Please fill in all fields');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/api-keys`, {
        email,
        organization_name: orgName,
        tier
      });
      setApiKey(response.data.api_key);
      toast.success('API key generated successfully!');
    } catch (error) {
      console.error('API key generation error:', error);
      toast.error('Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <nav className="fixed top-0 w-full z-50 glass border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Pricing</span>
          </button>
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_emotionengine/artifacts/i8r1k6ge_Black%20and%20White%20Bold%20Typographic%20Design%20Logo%20-%204.png"
              alt="EmoQ.ai"
              className="h-14 w-auto"
            />
            <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>API Documentation</h1>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Code className="w-16 h-16 mx-auto mb-4 text-indigo-600" />
            <h1 className="text-4xl font-bold mb-4" style={{fontFamily: 'Space Grotesk'}}>
              EmoQ.ai API
            </h1>
            <p className="text-lg text-gray-600">
              Integrate emotional intelligence analysis into your applications
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* API Key Generation */}
            <Card className="card-elevated lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Get API Key
                </CardTitle>
                <CardDescription>Generate your API credentials</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!apiKey ? (
                  <>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Organization</Label>
                      <Input
                        placeholder="Your Company"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Tier</Label>
                      <Select value={tier} onValueChange={setTier}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="startup">Startup ($50/1K)</SelectItem>
                          <SelectItem value="growth">Growth ($40/1K)</SelectItem>
                          <SelectItem value="enterprise">Enterprise ($30/1K)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleGenerateKey}
                      disabled={generating}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      {generating ? 'Generating...' : 'Generate API Key'}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
                      <p className="text-sm font-semibold mb-2">API Key Generated!</p>
                      <div className="bg-white p-3 rounded border border-gray-200 font-mono text-xs break-all mb-2">
                        {apiKey}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(apiKey)}
                        className="w-full"
                      >
                        Copy Key
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Keep your API key secure. It will only be shown once.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Documentation */}
            <Card className="card-elevated lg:col-span-2">
              <CardHeader>
                <CardTitle>Quick Start</CardTitle>
                <CardDescription>Integration guide for developers</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="analyze">
                  <TabsList>
                    <TabsTrigger value="analyze">Text Analysis</TabsTrigger>
                    <TabsTrigger value="image">Image Analysis</TabsTrigger>
                    <TabsTrigger value="response">Response</TabsTrigger>
                  </TabsList>

                  <TabsContent value="analyze" className="space-y-4">
                    <div>
                      <Label className="text-lg font-semibold mb-2 block">POST /api/analyze/text</Label>
                      <p className="text-sm text-gray-600 mb-4">Analyze emotional content of text</p>
                      
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                        <pre>{`const response = await fetch('${API}/analyze/text', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    text: "Your content here",
    audience_filters: {
      country: "US",
      gender: "All",
      age_range: "25-40",
      professions: ["Tech Leaders"]
    }
  })
});

const data = await response.json();`}</pre>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="image" className="space-y-4">
                    <div>
                      <Label className="text-lg font-semibold mb-2 block">POST /api/analyze/image</Label>
                      <p className="text-sm text-gray-600 mb-4">Analyze emotional content of images</p>
                      
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                        <pre>{`const formData = new FormData();
formData.append('file', imageFile);
formData.append('audience_filters', JSON.stringify({
  country: "US",
  gender: "All",
  age_range: "18-65"
}));

const response = await fetch('${API}/analyze/image', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: formData
});

const data = await response.json();`}</pre>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="response" className="space-y-4">
                    <div>
                      <Label className="text-lg font-semibold mb-2 block">Response Format</Label>
                      <p className="text-sm text-gray-600 mb-4">Emotional analysis results</p>
                      
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                        <pre>{`{
  "id": "analysis_id",
  "emotions": {
    "joy": 75,
    "trust": 60,
    "anticipation": 55,
    // ... other emotions
  },
  "arousal_level": 0.7,
  "valence": 0.8,
  "dominance": 0.6,
  "dominant_emotion": "joy",
  "audience_reactions": {
    "general": "Highly positive",
    "emotional_tone": "Excited",
    "engagement_level": "high"
  },
  "recommendations": [
    "Maintain positive tone",
    "Add more specifics"
  ]
}`}</pre>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="mt-8 p-6 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h3 className="font-semibold text-indigo-900 mb-2">Need Help?</h3>
                  <p className="text-sm text-indigo-700 mb-4">
                    Our team is here to help you integrate emotional intelligence into your product.
                  </p>
                  <Button variant="outline" className="border-indigo-600 text-indigo-600">
                    Contact Support
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocs;